/**
 * Blood Bowl Calculator - Probability Calculation Functions
 *
 * Pure functions for calculating dice roll probabilities and optimal reroll allocation.
 * Ported from the legacy calculator JavaScript.
 */

import type {
  ActionType,
  ActionResult,
  Player,
  ActionWithContext,
  GroupedActions,
  CalculationResults,
  ActionBreakdownItem,
  RerollType,
  SkillKey,
  BreakdownEntry,
  FormulaBreakdown,
  FormulaTerm,
} from '../types';
import { ACTION_CATEGORIES, SKILLS, getResultLabel } from '../constants';

// ============================================================================
// Base Probability Calculations
// ============================================================================

/**
 * D6 probabilities for target rolls (2+ through 6+)
 */
const D6_PROBS: Record<string, number> = {
  '2+': 5 / 6,
  '3+': 4 / 6,
  '4+': 3 / 6,
  '5+': 2 / 6,
  '6+': 1 / 6,
};

/**
 * 2D6 armor break probabilities
 */
const ARMOR_PROBS: Record<string, number> = {
  '7+': 21 / 36,
  '8+': 15 / 36,
  '9+': 10 / 36,
  '10+': 6 / 36,
};

/**
 * Injury roll probabilities
 */
const INJURY_PROBS: Record<string, number> = {
  stunned_plus: 1,
  ko_plus: 21 / 36,
  casualty: 10 / 36,
};

/**
 * Single block die result probabilities
 */
const BLOCK_RESULT_PROBS: Record<string, number> = {
  pushed_or_pow: 4 / 6,
  pow_only: 1 / 6,
  pow_no_dodge: 2 / 6,
  pow_or_both_down: 2 / 6,
  no_skull: 5 / 6,
};

/**
 * Get the base probability for an action and result
 */
export function getBaseProbability(action: ActionType, result: ActionResult): number {
  // D6 actions
  if (['dodge', 'gfi', 'pickup', 'pass', 'catch', 'throw_teammate', 'landing', 'roll'].includes(action)) {
    return D6_PROBS[result] ?? 0;
  }

  // Armor rolls (2D6)
  if (action === 'armor') {
    return ARMOR_PROBS[result] ?? 0;
  }

  // Injury rolls (2D6)
  if (action === 'injury') {
    return INJURY_PROBS[result] ?? 0;
  }

  // Block dice
  const blockBaseProb = BLOCK_RESULT_PROBS[result] ?? 0;

  switch (action) {
    case 'block_3d':
      // Pick best of 3 dice: 1 - (1-p)^3
      return 1 - Math.pow(1 - blockBaseProb, 3);
    case 'block_2d':
      // Pick best of 2 dice: 1 - (1-p)^2
      return 1 - Math.pow(1 - blockBaseProb, 2);
    case 'block_1d':
      // Single die
      return blockBaseProb;
    case 'block_-2d':
      // Opponent picks best of 2: p^2
      return Math.pow(blockBaseProb, 2);
    case 'block_-3d':
      // Opponent picks best of 3: p^3
      return Math.pow(blockBaseProb, 3);
    default:
      return 0;
  }
}

/**
 * Calculate probability with one reroll
 * P(success with reroll) = 1 - (1-p)^2
 */
export function calculateWithReroll(baseProb: number): number {
  return 1 - Math.pow(1 - baseProb, 2);
}

/**
 * Calculate probability with team reroll for a Loner player
 * Must pass Loner roll first, then can reroll
 */
export function calculateWithLonerReroll(baseProb: number, lonerValue: number): number {
  const lonerProb = (7 - lonerValue) / 6;
  // P(success) = p + (1-p) * lonerProb * p
  return baseProb + (1 - baseProb) * lonerProb * baseProb;
}

// ============================================================================
// Action Context Building
// ============================================================================

/**
 * Build action context with player information for calculation
 */
export function buildActionContext(
  action: { id: string; type: ActionType; result: ActionResult; rerollPreference: string },
  groupId: string,
  playerId: string | null,
  players: Record<string, Player>
): ActionWithContext {
  const player = playerId ? players[playerId] ?? null : null;
  const category = ACTION_CATEGORIES[action.type];
  const skillKey = category.skill;
  const hasSkill = player !== null && skillKey !== null && player.skills[skillKey] === true;

  return {
    aid: action.id,
    actionType: action.type,
    result: action.result,
    rerollPref: action.rerollPreference as 'auto' | 'none' | 'team',
    playerId,
    player,
    baseProb: getBaseProbability(action.type, action.result),
    skillKey,
    hasSkill,
    groupId,
  };
}

/**
 * Format action name for display
 */
export function formatActionName(action: ActionWithContext): string {
  const category = ACTION_CATEGORIES[action.actionType];
  const resultLabel = getResultLabel(action.result);
  const desc = `${category.nameFr} ${resultLabel}`;

  if (action.player) {
    return `#${action.player.number} - ${desc}`;
  }
  return desc;
}

// ============================================================================
// Main Calculation
// ============================================================================

/**
 * Represents a rerollable action for the joint calculation
 */
interface RerollableAction {
  index: number;
  baseProb: number;
  skillGroupKey: string | null; // null if no skill reroll available
  canUseTeamReroll: boolean;
  isLoner: boolean;
  lonerValue: number;
  context: ActionWithContext;
}

/**
 * Calculate the probability of success given a failure pattern and available rerolls.
 * Uses optimal allocation: skill rerolls first (within their groups), then team rerolls.
 *
 * @param failedIndices - Set of indices that failed on first roll
 * @param actions - All rerollable actions
 * @param skillRerollsAvailable - Map of skillGroupKey -> 1 (one per group)
 * @param teamRerollsAvailable - Number of team rerolls available
 * @returns Probability of all failed actions being rerolled successfully, or 0 if not enough rerolls
 */
function calculateRerollSuccessProbability(
  failedIndices: Set<number>,
  actions: RerollableAction[],
  skillRerollsAvailable: Map<string, number>,
  teamRerollsAvailable: number
): number {
  if (failedIndices.size === 0) return 1;

  // Clone the available rerolls
  const skillRerolls = new Map(skillRerollsAvailable);
  let teamRerolls = teamRerollsAvailable;

  // Track which failed actions we can cover
  const failedActions = Array.from(failedIndices).map((i) => actions[i]!);

  // Sort by priority: first those with skill rerolls, then those that can use team rerolls
  // This greedy approach is optimal because skill rerolls are "free" (don't consume team rerolls)
  const withSkill = failedActions.filter((a) => a.skillGroupKey !== null);
  const withoutSkill = failedActions.filter((a) => a.skillGroupKey === null);

  let successProb = 1;
  const coveredIndices = new Set<number>();

  // First, try to use skill rerolls
  for (const action of withSkill) {
    const key = action.skillGroupKey!;
    const available = skillRerolls.get(key) ?? 0;
    if (available > 0) {
      skillRerolls.set(key, available - 1);
      successProb *= action.baseProb; // Reroll success probability
      coveredIndices.add(action.index);
    }
  }

  // For uncovered skill actions, try team reroll
  for (const action of withSkill) {
    if (coveredIndices.has(action.index)) continue;
    if (!action.canUseTeamReroll) return 0; // Can't reroll this, fail
    if (teamRerolls <= 0) return 0; // No team rerolls left

    teamRerolls--;
    if (action.isLoner) {
      // Loner: must pass loner roll first
      const lonerProb = (7 - action.lonerValue) / 6;
      successProb *= lonerProb * action.baseProb;
    } else {
      successProb *= action.baseProb;
    }
    coveredIndices.add(action.index);
  }

  // Now handle actions without skill rerolls
  for (const action of withoutSkill) {
    if (!action.canUseTeamReroll) return 0; // Can't reroll this, fail
    if (teamRerolls <= 0) return 0; // No team rerolls left

    teamRerolls--;
    if (action.isLoner) {
      const lonerProb = (7 - action.lonerValue) / 6;
      successProb *= lonerProb * action.baseProb;
    } else {
      successProb *= action.baseProb;
    }
    coveredIndices.add(action.index);
  }

  return successProb;
}

/**
 * Result of joint probability calculation including formula breakdown
 */
interface JointProbabilityResult {
  probability: number;
  formula: FormulaBreakdown;
}

/**
 * Reroll allocation result for a single action
 */
interface RerollAllocation {
  index: number;
  rerollType: 'skill' | 'team' | null; // null if no reroll needed or not possible
}

/**
 * Determine how rerolls would be allocated for a given failure pattern.
 * Returns the reroll type used for each failed action.
 */
function allocateRerolls(
  failedIndices: Set<number>,
  actions: RerollableAction[],
  skillRerollsAvailable: Map<string, number>,
  teamRerollsAvailable: number
): RerollAllocation[] {
  if (failedIndices.size === 0) return [];

  const allocations: RerollAllocation[] = [];
  const skillRerolls = new Map(skillRerollsAvailable);
  let teamRerolls = teamRerollsAvailable;

  const failedActions = Array.from(failedIndices).map((i) => actions[i]!);
  const withSkill = failedActions.filter((a) => a.skillGroupKey !== null);
  const withoutSkill = failedActions.filter((a) => a.skillGroupKey === null);

  const coveredIndices = new Set<number>();

  // First: Use skill rerolls
  for (const action of withSkill) {
    const key = action.skillGroupKey!;
    const available = skillRerolls.get(key) ?? 0;
    if (available > 0) {
      skillRerolls.set(key, available - 1);
      allocations.push({ index: action.index, rerollType: 'skill' });
      coveredIndices.add(action.index);
    }
  }

  // Uncovered skill actions use team reroll
  for (const action of withSkill) {
    if (coveredIndices.has(action.index)) continue;
    if (!action.canUseTeamReroll || teamRerolls <= 0) {
      allocations.push({ index: action.index, rerollType: null });
      continue;
    }
    teamRerolls--;
    allocations.push({ index: action.index, rerollType: 'team' });
    coveredIndices.add(action.index);
  }

  // Actions without skill use team reroll
  for (const action of withoutSkill) {
    if (!action.canUseTeamReroll || teamRerolls <= 0) {
      allocations.push({ index: action.index, rerollType: null });
      continue;
    }
    teamRerolls--;
    allocations.push({ index: action.index, rerollType: 'team' });
  }

  return allocations;
}

/**
 * Generate a compact label for a failure pattern
 * Each action is represented by one character:
 * - ✓ = success without reroll
 * - ⟳ = success with skill reroll
 * - ↻ = success with team reroll
 */
function generatePatternLabel(
  mask: number,
  actions: RerollableAction[],
  skillRerollsAvailable: Map<string, number>,
  teamRerollsAvailable: number
): string {
  const n = actions.length;
  const failedIndices = new Set<number>();

  for (let i = 0; i < n; i++) {
    if ((mask & (1 << i)) !== 0) {
      failedIndices.add(i);
    }
  }

  // Get reroll allocations for failed actions
  const allocations = allocateRerolls(failedIndices, actions, skillRerollsAvailable, teamRerollsAvailable);
  const allocationMap = new Map(allocations.map((a) => [a.index, a.rerollType]));

  const parts: string[] = [];
  for (let i = 0; i < n; i++) {
    if ((mask & (1 << i)) !== 0) {
      // Action failed initially - check what reroll was used
      const rerollType = allocationMap.get(i);
      if (rerollType === 'skill') {
        parts.push('⟳'); // Skill reroll
      } else if (rerollType === 'team') {
        parts.push('↻'); // Team reroll
      } else {
        parts.push('✗'); // No reroll available
      }
    } else {
      parts.push('✓'); // Success without reroll
    }
  }

  return parts.join('');
}

/**
 * Enumerate all possible failure patterns and calculate total success probability.
 * This is O(2^n) but n is typically small (< 10 actions).
 * Returns both the probability and the formula breakdown.
 */
function calculateJointProbability(
  actions: RerollableAction[],
  skillRerollsAvailable: Map<string, number>,
  teamRerollsAvailable: number
): JointProbabilityResult {
  const n = actions.length;
  let totalProb = 0;
  const terms: FormulaTerm[] = [];

  // Enumerate all 2^n possible outcomes (each action either succeeds or fails first try)
  for (let mask = 0; mask < (1 << n); mask++) {
    const failedIndices = new Set<number>();
    let patternProb = 1;

    // Calculate probability of this specific pattern
    for (let i = 0; i < n; i++) {
      const action = actions[i]!;
      if ((mask & (1 << i)) !== 0) {
        // Action i fails first try
        failedIndices.add(i);
        patternProb *= 1 - action.baseProb;
      } else {
        // Action i succeeds first try
        patternProb *= action.baseProb;
      }
    }

    // Calculate probability that all failed actions can be successfully rerolled
    const rerollSuccess = calculateRerollSuccessProbability(
      failedIndices,
      actions,
      skillRerollsAvailable,
      teamRerollsAvailable
    );

    const contribution = patternProb * rerollSuccess;

    // Only add terms that contribute to the probability
    if (contribution > 0) {
      terms.push({
        value: contribution,
        label: generatePatternLabel(mask, actions, skillRerollsAvailable, teamRerollsAvailable),
      });
    }

    totalProb += contribution;
  }

  return {
    probability: totalProb,
    formula: {
      terms,
      total: totalProb,
    },
  };
}

/**
 * Calculate optimal probabilities with reroll allocation
 *
 * This function correctly handles the joint allocation of skill rerolls and team rerolls.
 * It enumerates all possible failure patterns and determines optimal reroll usage for each.
 *
 * Key insight: The previous implementation treated skill reroll groups independently from
 * team reroll allocation. This was incorrect because team rerolls can be used on skill-eligible
 * actions when the skill reroll has already been consumed by another action in the same group.
 *
 * It handles:
 * - Skill rerolls (one per player per skill type per turn)
 * - Shared skill rerolls across multiple actions
 * - Team rerolls that can be used on ANY rerollable action (including skill-eligible ones)
 * - Loner trait handling for team rerolls
 * - Optimal allocation based on preferences
 */
export function calculateProbabilities(
  actionGroups: Array<{
    id: string;
    playerId: string | null;
    actions: Array<{ id: string; type: ActionType; result: ActionResult; rerollPreference: string }>;
  }>,
  players: Record<string, Player>,
  teamRerolls: number
): CalculationResults {
  // Build grouped actions with context
  const groupedActions: GroupedActions[] = actionGroups.map((group) => ({
    gid: group.id,
    playerId: group.playerId,
    player: group.playerId ? players[group.playerId] ?? null : null,
    actions: group.actions.map((action) => buildActionContext(action, group.id, group.playerId, players)),
  }));

  // Flatten all actions and build rerollable action list
  const allActions: ActionWithContext[] = [];
  groupedActions.forEach((group) => {
    group.actions.forEach((action) => {
      allActions.push(action);
    });
  });

  // Separate actions that can be rerolled from those that can't
  const rerollableActions: RerollableAction[] = [];
  const noRerollActions: ActionWithContext[] = [];

  // Track skill groups for determining available skill rerolls
  const skillGroups = new Map<string, number>();

  allActions.forEach((action, index) => {
    if (action.rerollPref === 'none') {
      noRerollActions.push(action);
    } else {
      // Determine skill group key
      let skillGroupKey: string | null = null;
      if (action.hasSkill && action.playerId && action.skillKey) {
        skillGroupKey = `${action.playerId}_${action.skillKey}`;
        // Each skill group has exactly 1 skill reroll available
        skillGroups.set(skillGroupKey, 1);
      }

      const canUseTeamReroll = action.rerollPref === 'team' || action.rerollPref === 'auto';

      rerollableActions.push({
        index: rerollableActions.length,
        baseProb: action.baseProb,
        skillGroupKey,
        canUseTeamReroll,
        isLoner: action.player?.loner ?? false,
        lonerValue: action.player?.lonerValue ?? 4,
        context: action,
      });
    }
  });

  // Calculate probability for no-reroll actions (must all succeed first try)
  let noRerollProb = 1;
  noRerollActions.forEach((action) => {
    noRerollProb *= action.baseProb;
  });

  // Calculate joint probability for rerollable actions
  const jointResult = calculateJointProbability(rerollableActions, skillGroups, teamRerolls);

  // Total probability is the product
  const totalProb = noRerollProb * jointResult.probability;

  // Determine if we have shared skill rerolls (multiple actions in same skill group)
  // The formula breakdown is only useful when there are dependencies between actions
  const skillGroupCounts = new Map<string, number>();
  rerollableActions.forEach((ra) => {
    if (ra.skillGroupKey) {
      skillGroupCounts.set(ra.skillGroupKey, (skillGroupCounts.get(ra.skillGroupKey) ?? 0) + 1);
    }
  });
  const hasSharedSkillRerolls = Array.from(skillGroupCounts.values()).some((count) => count > 1);

  // Only show formula breakdown when there are shared skill rerolls
  // For simple cases (single action or independent rerolls), the formula adds no value
  let formula: FormulaBreakdown | null = null;
  if (hasSharedSkillRerolls) {
    if (noRerollProb < 1) {
      // Scale all terms by the no-reroll probability
      formula = {
        terms: jointResult.formula.terms.map((term) => ({
          ...term,
          value: term.value * noRerollProb,
        })),
        total: totalProb,
      };
    } else {
      formula = jointResult.formula;
    }
  }

  // Build breakdown for display (simplified for now)
  const breakdown: BreakdownEntry[] = [];

  // Group actions by skill group for display
  const playerSkillActions: Record<string, ActionWithContext[]> = {};
  const otherRerollableActions: ActionWithContext[] = [];

  rerollableActions.forEach((ra) => {
    if (ra.skillGroupKey) {
      if (!playerSkillActions[ra.skillGroupKey]) {
        playerSkillActions[ra.skillGroupKey] = [];
      }
      playerSkillActions[ra.skillGroupKey].push(ra.context);
    } else {
      otherRerollableActions.push(ra.context);
    }
  });

  // Add skill groups to breakdown
  Object.entries(playerSkillActions).forEach(([key, actions]) => {
    const parts = key.split('_');
    const playerId = parts[0] ?? '';
    const skillKey = parts[1] ?? '';
    const player = players[playerId];

    if (actions.length === 1) {
      const action = actions[0];
      if (!action) return;
      const p = action.baseProb;
      const probWithReroll = calculateWithReroll(p);

      breakdown.push({
        name: formatActionName(action),
        prob: probWithReroll,
        baseProb: p,
        rerollType: 'skill',
      });
    } else {
      const skillName = skillKey ? SKILLS[skillKey as SkillKey]?.nameFr : skillKey;
      const playerNum = player?.number ?? '?';
      const firstAction = actions[0];
      if (!firstAction) return;

      // Calculate the shared skill reroll probability for this group (used for display)
      const probs = actions.map((a) => a.baseProb);
      const prodAll = probs.reduce((acc, p) => acc * p, 1);
      let sumFailReroll = 0;
      for (let j = 0; j < probs.length; j++) {
        const probJ = probs[j] ?? 0;
        const othersSucceed = probs.reduce((acc, p, i) => (i === j ? acc : acc * p), 1);
        sumFailReroll += othersSucceed * (1 - probJ) * probJ;
      }
      const groupProb = prodAll + sumFailReroll;

      breakdown.push({
        name: `#${playerNum} - ${actions.length}x ${ACTION_CATEGORIES[firstAction.actionType].nameFr} [1 relance ${skillName}]`,
        prob: groupProb,
        rerollType: 'skill-shared',
        isGroup: true,
        actions: actions.map((a) => ({
          name: a.result as string,
          prob: a.baseProb,
          actionName: ACTION_CATEGORIES[a.actionType].nameFr,
          result: a.result,
        })),
      });
    }
  });

  // Add other rerollable actions to breakdown
  // For display purposes, we show them with team reroll if available
  let displayTeamRerollsUsed = 0;
  otherRerollableActions.forEach((action) => {
    const p = action.baseProb;
    let finalProb = p;
    let rerollType: RerollType = 'none';

    if (displayTeamRerollsUsed < teamRerolls) {
      displayTeamRerollsUsed++;
      rerollType = 'team';
      if (action.player?.loner) {
        finalProb = calculateWithLonerReroll(p, action.player.lonerValue);
      } else {
        finalProb = calculateWithReroll(p);
      }
    }

    breakdown.push({
      name: formatActionName(action),
      prob: finalProb,
      baseProb: action.baseProb,
      rerollType,
    });
  });

  // Add no-reroll actions to breakdown
  noRerollActions.forEach((action) => {
    breakdown.push({
      name: formatActionName(action),
      prob: action.baseProb,
      baseProb: action.baseProb,
      rerollType: 'none',
    });
  });

  // Convert breakdown to detailed format
  const detailedBreakdown = buildDetailedBreakdown(breakdown);

  // Estimate team rerolls used (this is approximate for the display)
  // The actual calculation considers all paths, but we report a typical scenario
  const estimatedTeamRerollsUsed = Math.min(teamRerolls, displayTeamRerollsUsed);

  return {
    totalProbability: totalProb,
    teamRerollsUsed: estimatedTeamRerollsUsed,
    breakdown: detailedBreakdown,
    formula,
  };
}

/**
 * Build detailed breakdown for display
 */
function buildDetailedBreakdown(breakdown: BreakdownEntry[]): ActionBreakdownItem[] {
  const detailedBreakdown: ActionBreakdownItem[] = [];
  let actionIndex = 0;
  let cumulativeProb = 1;

  breakdown.forEach((item) => {
    if (item.isGroup && item.actions) {
      // Group of actions with shared skill reroll - show each individually
      item.actions.forEach((subAction) => {
        actionIndex++;
        const probWithReroll = calculateWithReroll(subAction.prob);
        cumulativeProb *= probWithReroll;
        detailedBreakdown.push({
          index: actionIndex,
          name: `${item.name.split(' - ')[0]} - ${subAction.actionName} ${getResultLabel(subAction.result)}`,
          baseProb: subAction.prob,
          probWithReroll: probWithReroll,
          cumulativeProb: cumulativeProb,
          hasReroll: true,
          rerollType: 'skill',
          rerollNote: 'Compétence (partagée)',
        });
      });

      // Correct cumulative prob for shared reroll
      const groupCumulativeBefore =
        cumulativeProb / item.actions.reduce((acc, a) => acc * calculateWithReroll(a.prob), 1);
      cumulativeProb = groupCumulativeBefore * item.prob;

      // Update last action's cumulative to reflect true group probability
      const lastItem = detailedBreakdown[detailedBreakdown.length - 1];
      if (lastItem) {
        lastItem.cumulativeProb = cumulativeProb;
      }
    } else {
      actionIndex++;
      const hasReroll = item.rerollType === 'skill' || item.rerollType === 'team';

      cumulativeProb *= item.prob;

      detailedBreakdown.push({
        index: actionIndex,
        name: item.name,
        baseProb: item.baseProb ?? item.prob,
        probWithReroll: hasReroll ? item.prob : null,
        cumulativeProb: cumulativeProb,
        hasReroll: hasReroll,
        rerollType: item.rerollType,
        rerollNote:
          item.rerollType === 'skill'
            ? 'Compétence'
            : item.rerollType === 'team'
              ? 'Équipe'
              : null,
      });
    }
  });

  return detailedBreakdown;
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Check if an action type can use a skill reroll from a player
 */
export function canUseSkillReroll(actionType: ActionType, player: Player | null): boolean {
  if (!player) return false;

  const category = ACTION_CATEGORIES[actionType];
  if (!category.skill) return false;

  return player.skills[category.skill] === true;
}

/**
 * Get the skill key for an action type
 */
export function getSkillForAction(actionType: ActionType): SkillKey | null {
  return ACTION_CATEGORIES[actionType].skill;
}

/**
 * Determine what reroll type is available for an action
 */
export function getAvailableRerollType(
  action: ActionWithContext,
  usedSkillRerolls: Set<string>,
  teamRerollsUsed: number,
  teamRerollsAvailable: number
): RerollType {
  if (action.rerollPref === 'none') {
    return 'none';
  }

  // Check for skill reroll
  if (action.hasSkill && action.playerId && action.skillKey) {
    const skillKey = `${action.playerId}_${action.skillKey}`;
    if (!usedSkillRerolls.has(skillKey)) {
      return 'skill';
    }
  }

  // Check for team reroll
  if (action.rerollPref === 'team' || action.rerollPref === 'auto') {
    if (teamRerollsUsed < teamRerollsAvailable) {
      return 'team';
    }
  }

  return 'none';
}
